import {
    listFichadas as repoListFichadas,
    listFichadasHistorial as repoListFichadasHistorial,
    listFichadasAbiertas as repoListFichadasAbiertas,
    editFichada as repoEditFichada,
} from '../infrastructure/fichadas.repository.js';
import { detectAnomalies, splitMinutesByDate } from '../shared/timesheet-policy.js';
import { listLiquidacionesConfirmadasDeEmpleado as repoListLiquidacionesConfirmadas } from '../infrastructure/liquidaciones.repository.js';
import {
    jornadasAbiertasOlvidadas,
    mensajeJornadasAbiertasOlvidadas,
    rangoFechasJornada,
    liquidacionQueCubre,
    mensajeDiaLiquidado,
} from '../shared/fichadas-ui-policy.js';

/**
 * Servicio de fichadas para el panel de staff (Control de Horarios y
 * Liquidación de Sueldos, slice 3 — Auditoría). Orquesta el repositorio
 * (I/O) + la política pura de `timesheet-policy.js`; no contiene lógica de
 * Supabase.
 *
 * `motivo` (Requirement: Admin Fichada Create, Edit, and Close — "Every such
 * write MUST require a non-empty reason") se valida ACÁ, ANTES de tocar el
 * repositorio, para las 4 acciones (crear/editar/cerrar/anular): esto es
 * solo UX (no gastar el round-trip) — la RPC `admin_edit_fichada` vuelve a
 * validarlo server-side y es la que de verdad lo hace inviolable.
 *
 * `editar` puede devolver `ok:true` con `cambios: 0` (ningún campo cambió
 * realmente — ver docs/supabase/19-control-horarios-auditoria.sql): eso NO
 * es una corrección exitosa y este servicio lo traduce a un resultado
 * `ok:false`, para que la UI nunca lo presente como un guardado.
 *
 * F-E (owner decision 2026-09-27, guarda de plata): antes de la RPC, una
 * corrección cuyas fechas ART afectadas (las de la jornada ANTES y DESPUÉS
 * del cambio) caen dentro de una liquidación CONFIRMADA del empleado se
 * rechaza con el mensaje del owner. Es UX (ahorra el round-trip y explica
 * qué hacer): la guarda autoritativa es la de la RPC (migración 32, código
 * `dia_liquidado`, mapeado acá al MISMO mensaje). Para editar/cerrar/anular
 * el caller pasa `actual` (la fila tal como la ve: empleado, nombre,
 * horarios); sin `actual` no hay chequeo previo y decide la RPC. Una lectura
 * de liquidaciones que falla o no responde en `TOPE_RESPUESTA_MS`
 * falla CERRADA: nunca se escribe sin haber podido mirar, y el resultado es
 * `ok:false` con su propio mensaje (`DIA_LIQUIDADO_NO_VERIFICADO_MSG`), no el
 * genérico. El límite existe porque esa lectura no tiene timeout propio: un
 * cuelgue dejaría el flag de mutación en vuelo del caller trabado para
 * siempre y todo guardado posterior se ignoraría en silencio.
 */

/** Mensajes por código de error inline de `admin_edit_fichada` (RPC nunca lanza excepción cruda). */
const FICHADA_ERROR_MESSAGES = {
    payload_requerido: 'Los datos enviados no son válidos.',
    payload_invalido: 'Los datos enviados no son válidos.',
    accion_invalida: 'Acción inválida.',
    empleado_id_requerido: 'El empleado es obligatorio.',
    entrada_at_requerido: 'La hora de entrada es obligatoria.',
    empleado_inexistente: 'El empleado no existe.',
    fichada_id_requerido: 'La fichada es obligatoria.',
    fichada_inexistente: 'La fichada no existe.',
    fichada_anulada: 'Esa fichada fue anulada y no se puede modificar.',
    jornada_ya_cerrada: 'Esa jornada ya está cerrada.',
    jornada_abierta_existente: 'El empleado ya tiene una jornada abierta.',
    entrada_at_invalido: 'La hora de entrada no es válida.',
    motivo_requerido: 'El motivo es obligatorio.',
};

const FICHADA_GENERIC_ERROR_MSG = 'No pudimos guardar la fichada. Probá de nuevo.';
/** F-E: la lectura de liquidaciones falló o no respondió a tiempo; no se escribió nada. */
const DIA_LIQUIDADO_NO_VERIFICADO_MSG = 'No pudimos verificar si el día ya está liquidado. No se guardó nada; probá de nuevo.';
/**
 * Tope de espera de este servicio: la lectura previa de F-E
 * (`chequearDiaLiquidado`) y la RPC `admin_edit_fichada` (L9c). Mismo valor
 * que el tope de carga de sección de `js/admin.js` (`SECTION_LOAD_TIMEOUT_MS`).
 */
const TOPE_RESPUESTA_MS = 15_000;
/** `editar` con `cambios: 0`: la RPC corrió (`ok:true`) pero ningún campo cambió. */
const FICHADA_SIN_CAMBIOS_MSG = 'No se guardó ningún cambio: los valores ingresados son iguales a los actuales.';

/**
 * @param {string|undefined} code
 * @returns {string}
 */
function mapFichadaError(code) {
    return FICHADA_ERROR_MESSAGES[code] ?? FICHADA_GENERIC_ERROR_MSG;
}

/**
 * @typedef {{ ok: boolean, error?: string, incierto?: boolean, fichada_id?: string, accion?: string, cambios?: number, salida_at?: string }} FichadaEditResult
 */

/**
 * La fila como la ve el caller, para la guarda F-E. Nunca viaja a la RPC.
 * @typedef {{ empleado_id: string, empleadoNombre?: string, entrada_at: string, salida_at?: string|null }} FichadaActual
 */

/** `name` del error con el que `conLimite` rechaza al vencer el tope. */
const LIMITE_VENCIDO = 'LimiteVencidoError';

/**
 * Rechaza con un error `name === LIMITE_VENCIDO` si `promise` no se resuelve
 * en `ms` (mismo patrón que `conTimeoutDeCarga` de `js/admin.js`: la
 * respuesta tardía se descarta). El ÚNICO tope de este servicio: lo usan la
 * guarda F-E y la RPC de edición.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function conLimite(promise, ms) {
    let timer;
    const limite = new Promise((_, reject) => {
        timer = setTimeout(() => {
            const err = new Error(`La operación no respondió en ${ms} ms.`);
            err.name = LIMITE_VENCIDO;
            reject(err);
        }, ms);
    });
    return Promise.race([promise, limite]).finally(() => clearTimeout(timer));
}

/**
 * Guarda F-E: `null` si se puede seguir, o el mensaje de rechazo. UNA
 * lectura acotada (envolvente de los rangos); la superposición exacta la
 * decide `liquidacionQueCubre` rango por rango. Si la lectura falla o no
 * responde en `TOPE_RESPUESTA_MS`, rechaza con
 * `DIA_LIQUIDADO_NO_VERIFICADO_MSG` (falla cerrada, nunca lanza).
 * @param {{ empleadoId: string|undefined, empleadoNombre?: string, jornadas: Array<{ entrada_at?: string|null, salida_at?: string|null }|null> }} params
 * @returns {Promise<string|null>}
 */
async function chequearDiaLiquidado({ empleadoId, empleadoNombre, jornadas }) {
    const rangos = jornadas.map(rangoFechasJornada).filter(Boolean);
    if (!empleadoId || rangos.length === 0) return null;
    const desde = rangos.reduce((min, r) => (r.desde < min ? r.desde : min), rangos[0].desde);
    const hasta = rangos.reduce((max, r) => (r.hasta > max ? r.hasta : max), rangos[0].hasta);
    let liquidaciones;
    try {
        liquidaciones = await conLimite(repoListLiquidacionesConfirmadas({ empleadoId, desde, hasta }), TOPE_RESPUESTA_MS);
    } catch (err) {
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('chequearDiaLiquidado: no se pudo leer las liquidaciones', err);
        return DIA_LIQUIDADO_NO_VERIFICADO_MSG;
    }
    return liquidacionQueCubre(rangos, liquidaciones) ? mensajeDiaLiquidado(empleadoNombre) : null;
}

/**
 * Guarda F-E para una fila existente: la jornada ANTES (`actual`) y, si la
 * acción la cambia, DESPUÉS. `null` (sin chequeo previo) si no vino `actual`.
 * @param {FichadaActual|undefined} actual
 * @param {{ entrada_at?: string|null, salida_at?: string|null }|null} despues
 */
function guardaDeFila(actual, despues) {
    if (!actual) return null;
    return {
        empleadoId: actual.empleado_id,
        empleadoNombre: actual.empleadoNombre,
        jornadas: [{ entrada_at: actual.entrada_at, salida_at: actual.salida_at ?? null }, despues],
    };
}

/**
 * L9c — la RPC `admin_edit_fichada` tiene el tope `TOPE_RESPUESTA_MS`. Sin
 * él, una llamada colgada dejaba al panel con la corrección "en vuelo" para
 * siempre (`fichadaMutationInFlight` nunca se liberaba y ningún otro
 * guardado andaba). Al vencer NO se sabe si la escritura se aplicó: se
 * devuelve como incierto (`incierto: true`), nunca como un error "probá de
 * nuevo"; `js/admin.js` avisa y recarga la lista (R5).
 */
const FICHADA_EDIT_INCIERTO_MSG = 'No sabemos si se guardó la fichada — revisá la lista.';

/**
 * Valida `motivo` y envía el payload a `admin_edit_fichada` vía el
 * repositorio. Único punto por el que pasan las 4 acciones
 * (crear/editar/cerrar/anular).
 * @param {{ accion: 'crear'|'editar'|'cerrar'|'anular', motivo?: string } & Record<string, unknown>} payload
 * @param {{ empleadoId: string|undefined, empleadoNombre?: string, jornadas: Array<{ entrada_at?: string|null, salida_at?: string|null }|null> }|null} [guarda]
 *   Guarda F-E previa a la RPC (ver `chequearDiaLiquidado`): el empleado y
 *   las jornadas ANTES/DESPUÉS cuyas fechas ART no pueden caer en una
 *   liquidación confirmada. `null` = sin chequeo previo (decide la RPC). Si
 *   rechaza, devuelve `ok:false` sin llamar a la RPC.
 * @returns {Promise<FichadaEditResult>}
 */
async function submitFichadaEdit(payload, guarda = null) {
    const motivo = typeof payload.motivo === 'string' ? payload.motivo.trim() : '';
    if (!motivo) {
        return { ok: false, error: FICHADA_ERROR_MESSAGES.motivo_requerido };
    }

    if (guarda) {
        const rechazo = await chequearDiaLiquidado(guarda);
        if (rechazo) return { ok: false, error: rechazo };
    }

    let result;
    try {
        result = await conLimite(repoEditFichada({ ...payload, motivo }), TOPE_RESPUESTA_MS);
    } catch (err) {
        if (err?.name === LIMITE_VENCIDO) return { ok: false, incierto: true, error: FICHADA_EDIT_INCIERTO_MSG };
        throw err;
    }
    if (result?.error === 'dia_liquidado') {
        return { ok: false, error: mensajeDiaLiquidado(result.empleado_nombre) };
    }
    if (!result?.ok) {
        return { ok: false, error: mapFichadaError(result?.error) };
    }

    if (payload.accion === 'editar' && result.cambios === 0) {
        return { ok: false, error: FICHADA_SIN_CAMBIOS_MSG, cambios: 0 };
    }

    return { ok: true, ...result };
}

/**
 * Trae las fichadas del rango y calcula sus anomalías (Requirement: Anomaly
 * Detection), enriquecidas con lo necesario para renderizarlas sin que la
 * vista tenga que volver a mirar `fichadas` (nombre del empleado, horarios,
 * minutos totales — vía `splitMinutesByDate`, que reporta minutos EXACTOS,
 * sin redondear; el formateo para mostrar vive en la vista).
 *
 * `sinRango: true` (owner request, 2026-09-02: checkbox "Todo el historial"
 * en `FichadasView.js`) cambia de modo por completo: ignora `desde`/`hasta`
 * y trae TODAS las fichadas de UN empleado (`empleadoId`, obligatorio en
 * este modo — se valida ACÁ, antes de tocar el repositorio, mismo criterio
 * que `motivo` en `submitFichadaEdit`). Las anomalías se siguen calculando
 * sobre lo que vuelva, sin restricción de rango (`detectAnomalies` ya
 * soporta `desde`/`hasta` ausentes — ver su propio docstring).
 * `truncado` (review-6d2bc4aaef1ed2ce finding 2) reflects whether
 * `listFichadasHistorial`'s 2000-row hard cap was actually hit for a
 * `sinRango` load — always `false` for the range path, which has no cap.
 * Passed through unchanged from the repository so `FichadasView` can show a
 * neutral notice instead of silently hiding older rows.
 * L7: `fechasArt` (fechas ART `YYYY-MM-DD`, inclusive) acota las anomalías
 * por fecha ART; `desde`/`hasta` siguen siendo los límites ISO de la LECTURA
 * (`ventanaLecturaFichadas`). Sin `fechasArt`, las anomalías se acotan con
 * `desde`/`hasta` tal cual (comportamiento anterior).
 * @param {{ desde?: string, hasta?: string, fechasArt?: { desde: string, hasta: string }, now?: string|number|Date, empleadoId?: string, sinRango?: boolean }} params
 * @returns {Promise<{ fichadas: Array<object>, anomalies: Array<{ fichadaId: string, tipo: string, empleadoNombre: string, entrada_at: string, salida_at: string|null, minutosTotal: number }>, truncado: boolean }>}
 */
export async function loadFichadas({ desde, hasta, fechasArt, now, empleadoId, sinRango } = {}) {
    if (sinRango && !empleadoId) {
        throw new Error('loadFichadas: empleadoId requerido cuando sinRango es true');
    }

    const { fichadas, truncado } = sinRango
        ? await repoListFichadasHistorial({ empleadoId })
        : { fichadas: await repoListFichadas({ desde, hasta }), truncado: false };
    const rangoAnomalias = fechasArt ?? { desde, hasta };
    const rawAnomalies = sinRango
        ? detectAnomalies(fichadas, { now })
        : detectAnomalies(fichadas, { now, desde: rangoAnomalias.desde, hasta: rangoAnomalias.hasta });
    const byId = new Map(fichadas.map((fichada) => [fichada.id, fichada]));

    const anomalies = rawAnomalies.map(({ fichadaId, tipo }) => {
        const fichada = byId.get(fichadaId);
        const minutosTotal = fichada
            ? splitMinutesByDate(fichada).reduce((total, bucket) => total + bucket.minutos, 0)
            : 0;
        return {
            fichadaId,
            tipo,
            empleadoId: fichada?.empleado_id ?? null, // F-E: la guarda de día liquidado es por empleado
            empleadoNombre: fichada?.empleados?.nombre ?? '',
            entrada_at: fichada?.entrada_at ?? null,
            salida_at: fichada?.salida_at ?? null,
            minutosTotal,
        };
    });

    return { fichadas, anomalies, truncado };
}

/**
 * Alta manual de una fichada (Requirement: Admin Fichada Create, Edit, and
 * Close). Guarda F-E sobre la fecha de la entrada nueva (`empleadoNombre`
 * solo alimenta el mensaje, nunca viaja a la RPC).
 * @param {{ empleado_id: string, entrada_at: string, motivo: string, empleadoNombre?: string }} params
 * @returns {Promise<FichadaEditResult>}
 */
export async function crearFichada({ empleado_id, entrada_at, motivo, empleadoNombre } = {}) {
    return submitFichadaEdit(
        { accion: 'crear', empleado_id, entrada_at, motivo },
        { empleadoId: empleado_id, empleadoNombre, jornadas: [{ entrada_at, salida_at: null }] },
    );
}

/**
 * Edita `entrada_at`/`salida_at` de una fichada existente. Solo se envían las
 * claves presentes en `fields` — la RPC solo audita/toca el campo que
 * efectivamente viene en el payload (`p_payload ? 'entrada_at'`, migración
 * 19), así que omitir una clave significa "no tocar ese campo", no "vaciarlo".
 * `actual` (F-E) es la fila antes del cambio; nunca viaja a la RPC.
 * @param {{ fichada_id: string, entrada_at?: string, salida_at?: string|null, motivo: string, actual?: FichadaActual }} fields
 * @returns {Promise<FichadaEditResult>}
 */
export async function editarFichada({ fichada_id, entrada_at, salida_at, motivo, actual } = {}) {
    const payload = { accion: 'editar', fichada_id, motivo };
    if (entrada_at !== undefined) payload.entrada_at = entrada_at;
    if (salida_at !== undefined) payload.salida_at = salida_at;
    const despues = actual
        ? {
              entrada_at: entrada_at !== undefined ? entrada_at : actual.entrada_at,
              salida_at: salida_at !== undefined ? salida_at : (actual.salida_at ?? null),
          }
        : null;
    return submitFichadaEdit(payload, guardaDeFila(actual, despues));
}

/**
 * Cierra una jornada abierta (Requirement: Admin closes an open shift with a
 * reason). `salida_at` es opcional: la RPC usa `now()` si se omite — y la
 * guarda F-E usa "ahora" también, para chequear las mismas fechas.
 * @param {{ fichada_id: string, salida_at?: string, motivo: string, actual?: FichadaActual }} params
 * @returns {Promise<FichadaEditResult>}
 */
export async function cerrarFichada({ fichada_id, salida_at, motivo, actual } = {}) {
    const payload = { accion: 'cerrar', fichada_id, motivo };
    if (salida_at !== undefined) payload.salida_at = salida_at;
    const despues = actual ? { entrada_at: actual.entrada_at, salida_at: salida_at ?? new Date().toISOString() } : null;
    return submitFichadaEdit(payload, guardaDeFila(actual, despues));
}

/**
 * Anula una fichada (AD5: nunca DELETE, siempre `anulada=true` + auditoría).
 * Guarda F-E solo sobre las fechas que tenía (no hay "después").
 * @param {{ fichada_id: string, motivo: string, actual?: FichadaActual }} params
 * @returns {Promise<FichadaEditResult>}
 */
export async function anularFichada({ fichada_id, motivo, actual } = {}) {
    return submitFichadaEdit({ accion: 'anular', fichada_id, motivo }, guardaDeFila(actual, null));
}

/**
 * Jornadas abiertas hace más de 12 h para el banner del Panel Admin (F-B,
 * owner decision 2026-09-27). UNA lectura (`listFichadasAbiertas`) + la
 * política pura; el error de lectura se propaga a propósito: el caller
 * (`js/admin.js`) lo loguea y oculta el banner, nunca bloquea la página.
 *
 * `abiertas` son TODAS las filas leídas, crudas y de cualquier antigüedad:
 * el tablero de Liquidación reusa esta misma lectura para ver turnos
 * abiertos más viejos que su ventana de fichadas (`desde` menos el pad).
 * @param {{ now?: string|number|Date }} [params]
 * @returns {Promise<{ jornadas: Array<{ fichadaId: string, empleadoId: string, nombre: string, entrada_at: string }>, mensaje: string, abiertas: Array<object> }>}
 */
export async function loadJornadasAbiertasOlvidadas({ now = new Date() } = {}) {
    const abiertas = await repoListFichadasAbiertas();
    const jornadas = jornadasAbiertasOlvidadas(abiertas, now);
    return { jornadas, mensaje: mensajeJornadasAbiertasOlvidadas(jornadas), abiertas };
}
